import { memo, useCallback, useMemo, useState, type ChangeEvent } from 'react';
import { Tooltip } from '@/components/Tooltip';
import { getOfficerPluralLabel } from '@/utils/auth';
import { Dropdownlist } from '@/components/Dropdownlist';
import { OptionsEditor } from '../builder/OptionsEditor';
import { ColumnsEditor } from '../builder/ColumnsEditor';
import { FormulaEditor } from '../builder/FormulaEditor';
import { ShowIfPicker } from '../builder/ShowIfPicker';
import { isOptionType, slugLabel } from '../builder/schema-draft';
import type { FieldDraft } from '../builder/schema-draft';
import type { UseSchemaDraftResult } from '../builder/useSchemaDraft';
import type { FormFieldType } from '@/services/api-types';
import { attachToSupportsSearchable } from '@/models/form-schema';

export interface WysiwygFieldInspectorProps {
  field: FieldDraft;
  sectionUiId: string;
  fieldIndex: number;
  totalFieldsInSection: number;
  earlierFields: FieldDraft[];
  draft: UseSchemaDraftResult;
  onClose: () => void;
  onRequestSearchableToggle?: (field: FieldDraft) => void;
}

const TYPE_ITEMS: { value: FormFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'radio', label: 'Single choice' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkboxGroup', label: 'Checkboxes' },
  { value: 'table', label: 'Table' },
  { value: 'computed', label: 'Calculated' },
];

const LAYOUT_ITEMS = [
  { value: '', label: 'Label above the value' },
  { value: 'horizontal', label: 'Label and value side by side' },
];

const FORMAT_ITEMS = [
  { value: '', label: 'Plain number' },
  { value: 'currency', label: 'Currency ($)' },
];

/**
 * Right-side inspector for a selected field. The rendered field on the
 * canvas is never modified in place — selection just opens this panel.
 *
 * Owns the entire edit surface: type, label, flags (required / wide /
 * summary / searchable), layout, format, subheading, inline-with-previous,
 * key (advanced), per-type editors (options / columns + predefined rows /
 * formula), showIf, and the Move / Add-below / Delete actions.
 *
 * Uses project-standard primitives — Dropdownlist (not native `<select>`),
 * `kstc-form-group` / `kstc-label` / `kstc-input` / `kstc-textarea`,
 * `kstc-btn--success/secondary/danger` for actions.
 */
export const WysiwygFieldInspector = memo(function WysiwygFieldInspector({
  field,
  sectionUiId,
  fieldIndex,
  totalFieldsInSection,
  earlierFields,
  draft,
  onClose,
  onRequestSearchableToggle,
}: WysiwygFieldInspectorProps) {
  const officersLabel = useMemo(() => getOfficerPluralLabel().toLowerCase(), []);

  const handleLabelChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { label: e.target.value });
    },
    [draft, sectionUiId, field.uiId],
  );

  const handleTypeChange = useCallback(
    (val: string | number | null) => {
      if (typeof val !== 'string') return;
      draft.changeFieldType(sectionUiId, field.uiId, val as FormFieldType);
    },
    [draft, sectionUiId, field.uiId],
  );

  const handleRequiredToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { required: e.target.checked || undefined });
    },
    [draft, sectionUiId, field.uiId],
  );
  const handleWideToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { wide: e.target.checked || undefined });
    },
    [draft, sectionUiId, field.uiId],
  );
  const handleSummaryToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { summary: e.target.checked || undefined });
    },
    [draft, sectionUiId, field.uiId],
  );
  const handleTabToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { tab: e.target.checked || undefined });
    },
    [draft, sectionUiId, field.uiId],
  );
  const handleSearchableToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { searchable: e.target.checked || undefined });
    },
    [draft, sectionUiId, field.uiId],
  );
  const handleLayoutChange = useCallback(
    (val: string | number | null) => {
      const v = typeof val === 'string' ? val : '';
      draft.updateField(sectionUiId, field.uiId, { layout: v === 'horizontal' ? 'horizontal' : undefined });
    },
    [draft, sectionUiId, field.uiId],
  );
  const handleFormatChange = useCallback(
    (val: string | number | null) => {
      const v = typeof val === 'string' ? val : '';
      draft.updateField(sectionUiId, field.uiId, { format: v || undefined });
    },
    [draft, sectionUiId, field.uiId],
  );

  const handleKeyChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { key: e.target.value, keyLocked: true });
    },
    [draft, sectionUiId, field.uiId],
  );
  const handleKeyToggle = useCallback(() => {
    if (field.keyLocked) {
      draft.updateField(sectionUiId, field.uiId, { key: slugLabel(field.label), keyLocked: false });
    } else {
      draft.updateField(sectionUiId, field.uiId, { keyLocked: true });
    }
  }, [draft, sectionUiId, field.uiId, field.keyLocked, field.label]);

  const handleSubheadingChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { subheading: e.target.value });
    },
    [draft, sectionUiId, field.uiId],
  );
  const [hasSubheading, setHasSubheading] = useState(field.subheading !== undefined);
  const handleSubheadingToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const on = e.target.checked;
      setHasSubheading(on);
      draft.updateField(sectionUiId, field.uiId, { subheading: on ? '' : undefined });
    },
    [draft, sectionUiId, field.uiId],
  );

  const handleInlineToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateField(sectionUiId, field.uiId, { inlineWithPrevious: e.target.checked || undefined });
    },
    [draft, sectionUiId, field.uiId],
  );

  const handleMoveUp = useCallback(() => {
    draft.moveField(sectionUiId, field.uiId, -1);
  }, [draft, sectionUiId, field.uiId]);
  const handleMoveDown = useCallback(() => {
    draft.moveField(sectionUiId, field.uiId, 1);
  }, [draft, sectionUiId, field.uiId]);
  const handleDelete = useCallback(() => {
    draft.removeField(sectionUiId, field.uiId);
    onClose();
  }, [draft, sectionUiId, field.uiId, onClose]);

  const handleAddBelow = useCallback(() => {
    draft.addField(sectionUiId, 'text');
    requestAnimationFrame(() => {
      const section = draft.draft.sections.find((s) => s.uiId === sectionUiId);
      if (!section) return;
      const last = section.fields[section.fields.length - 1];
      if (!last) return;
      const targetIdx = fieldIndex + 1;
      const currentIdx = section.fields.length - 1;
      for (let i = currentIdx; i > targetIdx; i--) {
        draft.moveField(sectionUiId, last.uiId, -1);
      }
    });
  }, [draft, sectionUiId, fieldIndex]);

  const predefinedRowsText = useMemo(
    () => (field.predefinedRows ?? []).map((r) => r.label).join('\n'),
    [field.predefinedRows],
  );
  const handlePredefinedRowsChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const lines = e.target.value
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length === 0) {
        draft.updateField(sectionUiId, field.uiId, { predefinedRows: undefined });
        return;
      }
      const seen = new Set<string>();
      const rows = lines.map((label) => {
        let id = slugLabel(label);
        let suffix = 2;
        while (seen.has(id)) {
          id = `${slugLabel(label)}_${suffix++}`;
        }
        seen.add(id);
        return { id, label };
      });
      draft.updateField(sectionUiId, field.uiId, { predefinedRows: rows });
    },
    [draft, sectionUiId, field.uiId],
  );

  const showIfScope = useMemo(
    () => earlierFields.filter((f) => f.uiId !== field.uiId),
    [earlierFields, field.uiId],
  );
  const earlierNumeric = earlierFields.filter((f) => f.type === 'number' || f.type === 'computed');

  // Case report forms are not part of Records Search — the backend rejects a
  // schema whose fields are marked searchable, so the control is shown disabled
  // with an explanation rather than silently failing on save.
  const searchableAllowedForHost = attachToSupportsSearchable(draft.draft.attachTo);
  const supportsSearchable = field.type !== 'table';
  const supportsSummary = field.type !== 'table';
  const supportsTab = field.type !== 'table';
  const supportsLayout =
    field.type === 'computed' || field.type === 'number' || field.type === 'text' ||
    field.type === 'select' || field.type === 'date' || field.type === 'radio';
  const supportsFormat = field.type === 'computed' || field.type === 'number';
  const supportsSubheading = field.type !== 'table';
  const typeLabel = TYPE_ITEMS.find((t) => t.value === field.type)?.label ?? field.type;

  return (
    <aside className="wysiwyg-inspector" role="dialog" aria-label="Field details">
      <div className="wysiwyg-inspector-header">
        <span className="wysiwyg-inspector-kind">Field</span>
        <span className="wysiwyg-inspector-title">{field.label || 'untitled'}</span>
        <span className="wysiwyg-inspector-tag">{typeLabel}</span>
        <button
          type="button"
          className="wysiwyg-inspector-close"
          onClick={onClose}
          aria-label="Close"
        >×</button>
      </div>

      <div className="wysiwyg-inspector-actions">
        <button
          type="button"
          className="kstc-btn kstc-btn--secondary kstc-btn--sm"
          onClick={handleMoveUp}
          disabled={fieldIndex === 0}
          title="Move this field one position earlier"
        >↑ Move up</button>
        <button
          type="button"
          className="kstc-btn kstc-btn--secondary kstc-btn--sm"
          onClick={handleMoveDown}
          disabled={fieldIndex === totalFieldsInSection - 1}
          title="Move this field one position later"
        >↓ Move down</button>
        <button
          type="button"
          className="kstc-btn kstc-btn--secondary kstc-btn--sm"
          onClick={handleAddBelow}
          title="Add a new field directly after this one"
        >+ Add field below</button>
        <button
          type="button"
          className="kstc-btn kstc-btn--danger kstc-btn--sm"
          onClick={handleDelete}
          title="Remove this field from the form"
        >Delete</button>
      </div>

      <div className="wysiwyg-inspector-body">
        <div className="kstc-form-group">
          <label className="kstc-label" htmlFor={`wysiwyg-insp-label-${field.uiId}`}>Label</label>
          <input
            id={`wysiwyg-insp-label-${field.uiId}`}
            type="text"
            className="kstc-input"
            value={field.label}
            onChange={handleLabelChange}
            placeholder="Field label"
          />
        </div>

        <div className="kstc-form-group">
          <label className="kstc-label">Type</label>
          <Dropdownlist
            value={field.type}
            items={TYPE_ITEMS}
            sortItems={false}
            searchable={false}
            onChange={handleTypeChange}
          />
        </div>

        <div className="wysiwyg-inspector-flag-grid">
          <label className="wysiwyg-inspector-checkbox" title="The form can't be submitted until this field is filled in.">
            <input type="checkbox" checked={!!field.required} onChange={handleRequiredToggle} />
            {' '}Required
          </label>
          <label className="wysiwyg-inspector-checkbox" title="Stretch this field across the full width of the section instead of taking half.">
            <input type="checkbox" checked={!!field.wide} onChange={handleWideToggle} />
            {' '}Full width
          </label>
          {supportsSummary && (
            <label className="wysiwyg-inspector-checkbox" title="Show this field's value on the tour stop summary so it's visible at a glance.">
              <input type="checkbox" checked={!!field.summary} onChange={handleSummaryToggle} />
              {' '}Show on tour stop summary
            </label>
          )}
          {supportsTab && (
            <label
              className="wysiwyg-inspector-checkbox"
              title="Show this field on the tour stop tab for this form. A tab is added to the tour stop page automatically once any field on this form is set to show on it."
            >
              <input type="checkbox" checked={!!field.tab} onChange={handleTabToggle} />
              {' '}Show on tour stop tab
            </label>
          )}
          {supportsSearchable && !field.isPersisted && (
            <label
              className="wysiwyg-inspector-checkbox"
              title={searchableAllowedForHost ? "Filter records by this field's value in Records Search." : undefined}
            >
              <input
                type="checkbox"
                checked={searchableAllowedForHost && !!field.searchable}
                disabled={!searchableAllowedForHost}
                onChange={handleSearchableToggle}
              />
              {' '}Searchable in Records Search
              {!searchableAllowedForHost && (
                <>
                  {' '}
                  <Tooltip
                    content="Case report forms are not included in Records Search, so their fields cannot be made searchable."
                    position="top"
                  />
                </>
              )}
            </label>
          )}
        </div>

        {(supportsLayout || supportsFormat) && (
          <div className="kstc-form-row">
            {supportsLayout && (
              <div className="kstc-form-group kstc-form-group--half">
                <label className="kstc-label">
                  Layout{' '}
                  <Tooltip
                    content="Where to put the field's label. Side by side keeps the label on the left and value on the right, which works well for short numbers like a price or count. Above the value is the standard form layout for everything else."
                    position="top"
                  />
                </label>
                <Dropdownlist
                  value={field.layout ?? ''}
                  items={LAYOUT_ITEMS}
                  sortItems={false}
                  searchable={false}
                  onChange={handleLayoutChange}
                />
              </div>
            )}
            {supportsFormat && (
              <div className="kstc-form-group kstc-form-group--half">
                <label className="kstc-label">
                  Number format{' '}
                  <Tooltip
                    content="How the number is shown. Currency formats it as $X.XX (US dollars). Plain shows just the digits."
                    position="top"
                  />
                </label>
                <Dropdownlist
                  value={field.format ?? ''}
                  items={FORMAT_ITEMS}
                  sortItems={false}
                  searchable={false}
                  onChange={handleFormatChange}
                />
              </div>
            )}
          </div>
        )}

        {field.isPersisted && supportsSearchable && searchableAllowedForHost && onRequestSearchableToggle && (
          <div className="kstc-form-group">
            <label className="kstc-label">Searchable in Records Search</label>
            <p className="wysiwyg-inspector-help">
              {field.searchable
                ? 'Currently on. Records can be filtered by this field in Records Search. Turning it off may take a few seconds on forms with many submissions.'
                : 'Currently off. This field is hidden from Records Search. Turning it on may take a few seconds on forms with many submissions.'}
            </p>
            <button
              type="button"
              className="kstc-btn kstc-btn--secondary kstc-btn--sm"
              onClick={() => onRequestSearchableToggle(field)}
            >
              {field.searchable ? 'Turn off' : 'Turn on'}
            </button>
          </div>
        )}

        {supportsSubheading && (
          <div className="kstc-form-group">
            <label className="wysiwyg-inspector-checkbox">
              <input type="checkbox" checked={hasSubheading} onChange={handleSubheadingToggle} />
              {' '}Add a heading or divider above this field
            </label>
            {hasSubheading && (
              <input
                type="text"
                className="kstc-input wysiwyg-inspector-subheading"
                value={field.subheading ?? ''}
                onChange={handleSubheadingChange}
                placeholder="Heading text, or leave blank for just a divider line"
              />
            )}
            <label className="wysiwyg-inspector-checkbox" style={{ marginTop: 6 }} title="Render this field on the same row as the field above it instead of starting a new row.">
              <input type="checkbox" checked={!!field.inlineWithPrevious} onChange={handleInlineToggle} />
              {' '}Place on the same row as the field above
            </label>
          </div>
        )}

        {isOptionType(field.type) && (
          <div className="kstc-form-group">
            <label className="kstc-label">Options</label>
            <OptionsEditor
              options={field.options ?? []}
              onAdd={() => draft.addOption(sectionUiId, field.uiId)}
              onRemove={(idx) => draft.removeOption(sectionUiId, field.uiId, idx)}
              onMove={(idx, delta) => draft.moveOption(sectionUiId, field.uiId, idx, delta)}
              onUpdate={(idx, patch) => draft.updateOption(sectionUiId, field.uiId, idx, patch)}
            />
          </div>
        )}

        {field.type === 'table' && (
          <>
            <div className="kstc-form-group">
              <label className="kstc-label">Columns</label>
              <ColumnsEditor
                columns={field.columns ?? []}
                onAdd={() => draft.addColumn(sectionUiId, field.uiId)}
                onRemove={(columnUiId) => draft.removeColumn(sectionUiId, field.uiId, columnUiId)}
                onMove={(columnUiId, delta) => draft.moveColumn(sectionUiId, field.uiId, columnUiId, delta)}
                onUpdate={(columnUiId, patch) => draft.updateColumn(sectionUiId, field.uiId, columnUiId, patch)}
                onTypeChange={(columnUiId, type) => draft.changeColumnType(sectionUiId, field.uiId, columnUiId, type)}
                onAddOption={(columnUiId) => {
                  const col = (field.columns ?? []).find((c) => c.uiId === columnUiId);
                  if (!col) return;
                  const next = [...(col.options ?? []), { value: '', label: '' }];
                  draft.updateColumn(sectionUiId, field.uiId, columnUiId, { options: next });
                }}
                onRemoveOption={(columnUiId, idx) => {
                  const col = (field.columns ?? []).find((c) => c.uiId === columnUiId);
                  if (!col) return;
                  const next = (col.options ?? []).filter((_, i) => i !== idx);
                  draft.updateColumn(sectionUiId, field.uiId, columnUiId, { options: next });
                }}
                onMoveOption={(columnUiId, idx, delta) => {
                  const col = (field.columns ?? []).find((c) => c.uiId === columnUiId);
                  if (!col) return;
                  const opts = (col.options ?? []).slice();
                  const target = Math.max(0, Math.min(idx + delta, opts.length - 1));
                  const [item] = opts.splice(idx, 1);
                  opts.splice(target, 0, item);
                  draft.updateColumn(sectionUiId, field.uiId, columnUiId, { options: opts });
                }}
                onUpdateOption={(columnUiId, idx, patch) => {
                  const col = (field.columns ?? []).find((c) => c.uiId === columnUiId);
                  if (!col) return;
                  const next = (col.options ?? []).map((o, i) => (i === idx ? { ...o, ...patch } : o));
                  draft.updateColumn(sectionUiId, field.uiId, columnUiId, { options: next });
                }}
              />
            </div>
            <div className="kstc-form-group">
              <label className="kstc-label">Fixed rows (optional)</label>
              <p className="wysiwyg-inspector-help">
                Type one row name per line. These rows will appear already in place on the form,
                and only the cells are editable. Leave blank to let {officersLabel} add as many
                rows as they need.
              </p>
              <textarea
                className="kstc-textarea"
                value={predefinedRowsText}
                onChange={handlePredefinedRowsChange}
                rows={Math.max(3, (field.predefinedRows?.length ?? 0) + 1)}
                placeholder={'e.g.\nLawn Care\nPool/Spa\nPest Control'}
              />
            </div>
          </>
        )}

        {field.type === 'computed' && (
          <div className="kstc-form-group">
            <label className="kstc-label">Formula</label>
            <FormulaEditor
              formula={field.formula ?? ''}
              availableIdentifiers={earlierNumeric}
              onChange={(formula) => draft.setFormula(sectionUiId, field.uiId, formula)}
            />
          </div>
        )}

        {field.type !== 'table' && (
          <div className="kstc-form-group">
            <label className="kstc-label">
              Only show this field when…{' '}
              <Tooltip
                content="Hide this field until an earlier field has a specific value. For example, only ask 'How many pets?' when 'Are there pets on site?' is Yes."
                position="top"
              />
            </label>
            <ShowIfPicker
              earlierFields={showIfScope}
              showIf={field.showIf}
              onChange={(next) => draft.setShowIf(sectionUiId, field.uiId, next)}
            />
          </div>
        )}

        <details className="wysiwyg-inspector-advanced">
          <summary className="wysiwyg-inspector-advanced-summary">Advanced</summary>
          <div className="wysiwyg-inspector-advanced-body">
            <div className="kstc-form-group">
              <label className="kstc-label">
                Internal name{' '}
                <Tooltip content="The system name used to store this field's value. Auto-generated from the label and rarely needs to change. If you rename the label later, the internal name updates automatically (unless you've customized it here)." position="top" />
              </label>
              <div className="wysiwyg-inspector-key-row">
                <input
                  type="text"
                  className="kstc-input"
                  value={field.key}
                  onChange={handleKeyChange}
                  placeholder="Generated from the label"
                  readOnly={!field.keyLocked}
                  aria-readonly={!field.keyLocked}
                />
                <button
                  type="button"
                  className="kstc-btn kstc-btn--secondary kstc-btn--sm"
                  onClick={handleKeyToggle}
                  title={field.keyLocked
                    ? 'Stop using a custom internal name and let it follow the label again.'
                    : 'Set a custom internal name (it will stop following the label).'}
                >
                  {field.keyLocked ? 'Use auto' : 'Customize'}
                </button>
              </div>
              <div className="wysiwyg-inspector-key-mode">
                {field.keyLocked
                  ? 'Custom. Set independently of the label.'
                  : 'Auto. Kept in sync with the label.'}
              </div>
            </div>
          </div>
        </details>
      </div>
    </aside>
  );
});
